import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

/**
 * View Role override for SuperAdmin preview.
 *
 * Three view levels exist:
 *   - 'agent'       — what an account agent sees
 *   - 'admin'       — what an account administrator sees
 *   - 'super_admin' — full developer/superadmin view (developer mode)
 *
 * Only the real SuperAdmin (currentUser.type === 'SuperAdmin') can switch
 * between views. Every other user is locked to their real account role.
 *
 * The selected view is persisted in `ui_settings.view_role_override`.
 *
 * Backwards-compat exports kept for existing call sites:
 *   - `isAdmin`            → real type is SuperAdmin (can toggle views)
 *   - `isDeveloperMode`    → current view === 'super_admin'
 *   - `toggleDeveloperMode`→ flips between 'super_admin' and 'admin'
 */

const VIEW_ROLES = ['agent', 'admin', 'super_admin'];

const realRoleFor = (user, accountRole) => {
  if (user?.type === 'SuperAdmin') return 'super_admin';
  if (accountRole === 'administrator') return 'admin';
  return 'agent';
};

export function useDeveloperMode() {
  const getters = useStoreGetters();
  const { uiSettings, updateUISettings } = useUISettings();

  const currentUser = computed(() => getters.getCurrentUser.value);
  const currentRole = computed(() => getters.getCurrentRole.value);

  // Only the real SuperAdmin instance user may switch views.
  const isAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

  const realRole = computed(() =>
    realRoleFor(currentUser.value, currentRole.value)
  );

  // Effective view: stored override (only honored for SuperAdmin) or the
  // user's real role.
  const viewRole = computed(() => {
    if (!isAdmin.value) return realRole.value;
    const stored = uiSettings.value.view_role_override;
    return VIEW_ROLES.includes(stored) ? stored : 'super_admin';
  });

  const setViewRole = nextRole => {
    if (!isAdmin.value) return;
    if (!VIEW_ROLES.includes(nextRole)) return;
    updateUISettings({ view_role_override: nextRole });
  };

  const isDeveloperMode = computed(() => viewRole.value === 'super_admin');

  // Legacy toggle: flips between developer (super_admin) and admin views.
  const toggleDeveloperMode = () => {
    if (!isAdmin.value) return;
    setViewRole(viewRole.value === 'super_admin' ? 'admin' : 'super_admin');
  };

  return {
    // legacy
    isAdmin,
    isDeveloperMode,
    toggleDeveloperMode,
    // new
    viewRole,
    setViewRole,
    realRole,
    VIEW_ROLES,
  };
}
